import React from 'react';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import ContactForm from './ContactForm';
import MapComponent from './GoogleMap';
import { useTranslation } from 'react-i18next';

const Contact = () => {
  const { t } = useTranslation('home');
  return (
    <>
      <section id="contact-form" className="py-20 bg-dental-light/5 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-full h-20 bg-gradient-to-b from-white to-dental-light/5"></div>
        <div className="absolute top-40 left-10 w-72 h-72 bg-dental-mint/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-60 h-60 bg-dental/10 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4 text-gradient-primary inline-block relative after:content-[''] after:absolute after:-bottom-2 after:left-1/4 after:right-1/4 after:h-1 after:bg-dental-tertiary after:rounded-full reveal" data-direction="up">
              {t('contact.heading')}
            </h2>
            <p className="text-xl text-dental-dark/80 max-w-3xl mx-auto reveal" data-direction="up">
              {t('contact.subheading')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="reveal" data-direction="left">
              {/* Contact Info Section */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-dental-gray/10">
                <h3 className="text-2xl font-display font-bold mb-6 text-dental-dark">{t('contact.cardTitle')}</h3>

                <div className="space-y-6">
                  <div className="flex items-start">
                    <Phone className="w-5 h-5 mt-1 text-dental" />
                    <div className="ml-4">
                      <h4 className="font-medium text-dental-dark">{t('contact.labels.phone')}</h4>
                      <a
                        href={`tel:${t('contact.phone.telHref')}`}
                        className="text-dental-dark hover:text-dental transition-colors duration-300"
                      >
                        {t('contact.phone.display')}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Mail className="w-5 h-5 mt-1 text-dental" />
                    <div className="ml-4">
                      <h4 className="font-medium text-dental-dark">{t('contact.labels.email')}</h4>
                      <p className="text-dental-dark/80">{t('contact.email.value')}</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <MapPin className="w-5 h-5 mt-1 text-dental" />
                    <div className="ml-4">
                      <h4 className="font-medium text-dental-dark">{t('contact.labels.address')}</h4>
                      <p className="text-dental-dark/80">
                        {t('contact.address.line1')} <br />
                        {t('contact.address.line2')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Clock className="w-5 h-5 mt-1 text-dental" />
                    <div className="ml-4">
                      <h4 className="font-medium text-dental-dark">{t('contact.labels.workingHours')}</h4>
                      <p className="text-dental-dark/80">
                        {t('contact.workingHours.line1')} <br />
                        {t('contact.workingHours.line2')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Map Component */}
                <div className="mt-8">
                  <MapComponent />
                </div>
              </div>
            </div>

            <div className="reveal" data-direction="right">
              <ContactForm title={t('contact.form.title')} />
            </div>
          </div>
        </div>
      </section>

      {/* Sticky Mobile Call Button */}
      <div className="fixed bottom-6 left-6 md:hidden z-50">
        <a
          href={`tel:${t('contact.phone.telHref')}`}
          className="flex items-center justify-center gap-2 bg-dental text-white px-4 py-3 rounded-full shadow-lg hover:bg-dental-dark transition-colors duration-300"
        >
          <Phone className="w-5 h-5" />
          <span className="font-medium">{t('contact.stickyCallText')}</span>
        </a>
      </div>
    </>
  );
};

export default Contact;
